import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { frappePost } from '@/lib/frappe';
import type {
  Course,
  CourseListItem,
  CourseListResponse,
  CourseFilters,
  TeacherCourseListItem,
} from '@/types/api';

// Query keys
export const courseKeys = {
  all: ['courses'] as const,
  list: (filters?: CourseFilters) => [...courseKeys.all, 'list', filters] as const,
  detail: (nameOrSlug: string) => [...courseKeys.all, 'detail', nameOrSlug] as const,
  featured: () => [...courseKeys.all, 'featured'] as const,
  categories: () => [...courseKeys.all, 'categories'] as const,
  teacherCourses: () => [...courseKeys.all, 'teacher'] as const,
  teacherCourse: (name: string) => [...courseKeys.all, 'teacher', name] as const,
};

// Get courses list (public)
export function useCourses(filters?: CourseFilters) {
  return useQuery({
    queryKey: courseKeys.list(filters),
    queryFn: () =>
      frappePost<CourseListResponse>(
        'hudan_lms.hudan_lms.doctype.hudan_course.hudan_course.get_courses',
        {
          category: filters?.category,
          level: filters?.level,
          instructor: filters?.instructor,
          featured: filters?.featured,
          search: filters?.search,
          page: filters?.page || 1,
          limit: filters?.limit || 12,
          sort_by: filters?.sort_by || 'modified',
          sort_order: filters?.sort_order || 'desc',
        },
        false // Allow guest access
      ),
  });
}

// Get single course detail (public)
export function useCourse(nameOrSlug: string, isSlug: boolean = false) {
  return useQuery({
    queryKey: courseKeys.detail(nameOrSlug),
    queryFn: () =>
      frappePost<Course>(
        'hudan_lms.hudan_lms.doctype.hudan_course.hudan_course.get_course',
        isSlug ? { slug: nameOrSlug } : { name: nameOrSlug },
        false
      ),
    enabled: !!nameOrSlug,
  });
}

// Get featured courses (public)
export function useFeaturedCourses(limit: number = 6) {
  return useQuery({
    queryKey: courseKeys.featured(),
    queryFn: () =>
      frappePost<CourseListResponse>(
        'hudan_lms.hudan_lms.doctype.hudan_course.hudan_course.get_featured_courses',
        { limit },
        false
      ),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Get course categories with counts (public)
export function useCourseCategories() {
  return useQuery({
    queryKey: courseKeys.categories(),
    queryFn: () =>
      frappePost<{ category: string; count: number }[]>(
        'hudan_lms.hudan_lms.doctype.hudan_course.hudan_course.get_course_categories',
        {},
        false
      ),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

// Get teacher's own courses
export function useTeacherCourses() {
  return useQuery({
    queryKey: courseKeys.teacherCourses(),
    queryFn: () =>
      frappePost<{ courses: TeacherCourseListItem[]; total: number }>(
        'hudan_lms.hudan_lms.doctype.hudan_course.hudan_course.get_teacher_courses'
      ),
  });
}

// Get one of the teacher's own courses with full editable fields
// (works for Draft/Pending Review, unlike the public get_course)
export function useTeacherCourse(courseName: string | null) {
  return useQuery({
    queryKey: courseKeys.teacherCourse(courseName || ''),
    queryFn: () =>
      frappePost<Partial<Course> & { name: string }>(
        'hudan_lms.hudan_lms.doctype.hudan_course.hudan_course.get_teacher_course',
        { course_name: courseName }
      ),
    enabled: !!courseName,
  });
}

// Enroll in a course
export function useEnrollInCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (courseName: string) =>
      frappePost<{ success: boolean; message: string }>(
        'hudan_lms.hudan_lms.doctype.hudan_course.hudan_course.enroll_in_course',
        { course_name: courseName }
      ),
    onSuccess: (_, courseName) => {
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(courseName) });
    },
  });
}

// Create a course (for teachers)
export function useCreateCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Course>) =>
      frappePost<{ success: boolean; course: string }>(
        'hudan_lms.hudan_lms.doctype.hudan_course.hudan_course.create_course',
        { data: JSON.stringify(data) }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courseKeys.teacherCourses() });
    },
  });
}

// Update a course (for teachers)
export function useUpdateCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ courseName, data }: { courseName: string; data: Partial<Course> }) =>
      frappePost<{ success: boolean; course: string }>(
        'hudan_lms.hudan_lms.doctype.hudan_course.hudan_course.update_course',
        { course_name: courseName, data: JSON.stringify(data) }
      ),
    onSuccess: (_, { courseName }) => {
      queryClient.invalidateQueries({ queryKey: courseKeys.detail(courseName) });
      queryClient.invalidateQueries({ queryKey: courseKeys.teacherCourses() });
    },
  });
}
